import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, FileText, ArrowRight, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { API_BASE_URL, BACKEND_BASE_URL } from "../../config/api";
import { TOSS_LOGO_URL as LOGO_URL, BRAND } from "@/lib/branding";

const API = API_BASE_URL;

export default function BasicDashboard() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [aRes, bmRes] = await Promise.all([
        axios.get(`${BACKEND_BASE_URL}/api/basic/assessments`),
        axios.get(`${BACKEND_BASE_URL}/api/basic/material-benchmarks`),
      ]);
      setAssessments(aRes.data);
      setSeeded(Object.keys(bmRes.data || {}).length > 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      // ensure advanced seed exists first (basic auto-derives from it)
      const matCheck = await axios.get(`${API}/materials`);
      if (!matCheck.data || matCheck.data.length === 0) {
        await axios.post(`${API}/seed`);
      }
      const r = await axios.post(`${BACKEND_BASE_URL}/api/basic/seed`);
      toast.success(`Basic data seeded: ${r.data.basic_material_benchmarks} material rows, ${r.data.basic_product_benchmarks} product stage benchmarks`);
      setSeeded(true);
      await loadData();
    } catch (e) {
      toast.error("Seed failed: " + (e.response?.data?.detail || e.message));
    } finally { setSeeding(false); }
  };

  const mixedMethods = new Set(assessments.map(a => a.results?.methodology_version || "legacy")).size > 1;
  const avgUpsi = assessments.length > 0
    ? (mixedMethods ? "Mixed methods" : (10 * assessments.reduce((s, a) => s + (a.results?.upsi || 0), 0) / assessments.length).toFixed(2)) : "N/A";

  const getScoreColor = (s) => s >= 7 ? "text-[#388E3C]" : s >= 4 ? "text-[#F57F17]" : "text-[#D32F2F]";

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="basic-dashboard">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <img src={LOGO_URL} alt={BRAND.short} className="h-12 w-auto object-contain" />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>
                Basic Dashboard
              </h1>
              <Badge className="bg-[#43A047] text-white tracking-wider text-[10px]"><Zap className="w-3 h-3 mr-1" />BASIC MODE</Badge>
            </div>
          </div>
        </div>
        <p className="text-base text-[#617364] mt-2">
          Simplified 5-stage {BRAND.short} — Raw Material &amp; EOL auto-derived from BOM, global transparency, fast scoring.
        </p>
      </div>

      {!seeded && (
        <Card className="mb-8 border-[#E0E6E1] bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Initialize Basic Benchmarks</h3>
                <p className="text-sm text-[#617364] mt-1">Auto-derives collapsed Raw Material &amp; EOL material data + 3-stage product benchmarks from the Advanced dataset.</p>
              </div>
              <Button onClick={handleSeed} disabled={seeding} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="basic-seed-btn">
                {seeding ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Seeding...</> : "Seed Basic Data"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-[#E0E6E1] bg-white"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-[0.2em] font-bold text-[#617364]">Total Assessments</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }} data-testid="basic-total">{assessments.length}</p></CardContent></Card>
        <Card className="border-[#E0E6E1] bg-white"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-[0.2em] font-bold text-[#617364]">Average TOSS /100</CardTitle></CardHeader>
          <CardContent><p className={`text-3xl font-light ${assessments.length > 0 ? getScoreColor(parseFloat(avgUpsi)/10) : 'text-[#1B291D]'}`} style={{ fontFamily: 'Work Sans' }} data-testid="basic-avg">{avgUpsi}</p></CardContent></Card>
        <Card className="border-[#E0E6E1] bg-white"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-[0.2em] font-bold text-[#617364]">Quick Action</CardTitle></CardHeader>
          <CardContent><Button onClick={() => navigate("/basic/new")} className="bg-[#43A047] hover:bg-[#388E3C] text-white w-full" data-testid="basic-new-btn"><PlusCircle className="w-4 h-4 mr-2" />New Basic Assessment</Button></CardContent></Card>
      </div>

      {assessments.length > 0 && (
        <Card className="border-[#E0E6E1] bg-white">
          <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Recent Basic Assessments</CardTitle>
            <Button variant="ghost" onClick={() => navigate("/basic/assessments")} className="text-[#43A047] text-sm">View All <ArrowRight className="w-4 h-4 ml-1" /></Button></div></CardHeader>
          <CardContent><div className="space-y-3">
            {assessments.slice(0, 5).map(a => (
              <div key={a.id} onClick={() => navigate(`/basic/assessments/${a.id}`)} className="flex items-center justify-between p-3 rounded-lg border border-[#E0E6E1] hover:bg-[#F8F9F8] cursor-pointer" data-testid={`basic-assessment-${a.id}`}>
                <div className="flex items-center gap-3"><FileText className="w-4 h-4 text-[#617364]" />
                  <div><p className="text-sm font-medium text-[#1B291D]">{a.product_name}</p><p className="text-xs text-[#617364]">{new Date(a.created_at).toLocaleDateString()}</p></div></div>
                <div className="flex items-center gap-3"><Badge variant="outline" className="border-[#E0E6E1] text-[#617364] text-xs">{a.product_type}</Badge>
                  {a.results?.upsi != null && <span className={`text-lg font-medium ${getScoreColor(a.results.upsi)}`} style={{ fontFamily: 'Work Sans' }}>{(a.results.upsi * 10).toFixed(2) + " /100"}</span>}</div>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}
    </div>
  );
}
